import React from "react";
import styles from "./styles.module.scss";
import { GroupSelectProps } from "./index.types";
import no_team_png from "app/images/null-training-space.png";
import UserAvatar from "app/components/UserAvatar";
import { cls } from "app/utils";
import useTeamSelectModal from "./indexModel";
import TeamModel from "app/models/TeamModel";
import TeamGroupModel from "app/models/TeamGroupModel";
import NoResults from "../NoResults";
import ApplyTemplateListsModal from "../ApplyTemplateListsModal";
import { useTranslation } from "react-i18next";
import { Loading } from "app/components/Wrappers";

const ChannelSelectModal = ({
  onClose,
  isOpen,
  templateId,
}: GroupSelectProps) => {
  const { t } = useTranslation();
  const {
    channelSelectHandler,
    cancelHandler,
    applyHandler,
    onSearchChannel,
    team,
    searchTerm,
    results,
    selectedChannelsId,
  } = useTeamSelectModal({ onClose, templateId });

  if (!isOpen) {
    return null;
  }

  // A team can reach the results with zero channels (e.g. its name matched
  // the search), and the list below skips those, so "no results" means no
  // team with at least one channel to show.
  const hasVisibleResults =
    Array.isArray(results) &&
    results.some(
      (team: TeamModel) =>
        team && Array.isArray(team.teamGroups) && team.teamGroups.length > 0,
    );

  // data == null only means "still loading" while the fetch hasn't failed;
  // after @team.fetch.error, data stays null with pending false.
  const isLoading = team.pending || (team.data == null && !team.error);

  return (
    <ApplyTemplateListsModal
      applyHandler={applyHandler}
      cancelHandler={cancelHandler}
      hasNoResult={
        searchTerm.length === 0 &&
        !team.pending &&
        team.data != null &&
        team.data.length === 0
      }
      noResultImage={no_team_png}
      noResultTitle={t("No Channels")}
      noResultSubText={t("You don't have created a channel yet")}
      isOpen={isOpen}
      onClose={onClose}
      searchPlaceholder={t("Search Channel...")}
      onSearchChange={onSearchChannel}
      modalSubTitle={t(
        "You can apply this Template to the Channel and posts will be added in the set interval and order",
      )}
    >
      <div className={styles.team_list_container}>
        {isLoading && <Loading isLoading={isLoading} loadType="spinner" />}
        {!isLoading && searchTerm.length > 0 && !hasVisibleResults && (
          <NoResults
            text={t('No results found for "{{keyword}}"', {
              keyword: searchTerm,
            })}
          />
        )}
        {!isLoading &&
          results &&
          results.map((team: TeamModel) => {
            if (
              team &&
              Array.isArray(team.teamGroups) &&
              team.teamGroups.length > 0
            ) {
              return (
                <div
                  key={team.id}
                  className={cls(styles.team_channel_container)}
                >
                  <div className={styles.team_container}>
                    <div className={styles.avatar}>
                      <UserAvatar
                        sizeWidth={44}
                        sizeHeight={44}
                        className="avatar"
                        userName={team.name}
                        srcName={team.logo}
                        altName={team.name}
                      />
                    </div>
                    <div className={styles.team}>
                      <div className={cls(styles.team_title, styles.elipsis)}>
                        {team.name}
                      </div>
                      <div
                        className={cls(styles.team_subtitle, styles.elipsis)}
                      >
                        {team.sport}
                      </div>
                    </div>
                  </div>
                  <div className={styles.channel_container}>
                    {team.teamGroups.map((channel: TeamGroupModel) => {
                      // Applying a template posts into the channel, so the
                      // channel's own permission gates both the disabled
                      // style and the click.
                      const channelCanPost =
                        channel.planPermissions?.includes("post") ?? false;

                      return (
                        <div key={channel.id}>
                          <div className={styles.channel}></div>

                          <div
                            className={cls(
                              styles.team_container,
                              styles.channel_hover,
                              {
                                [styles.disabled]: !channelCanPost,
                              },
                            )}
                            onClick={() =>
                              channelCanPost
                                ? channelSelectHandler(
                                    channel.id as string,
                                    channel,
                                  )
                                : null
                            }
                          >
                            <UserAvatar
                              sizeWidth={44}
                              sizeHeight={44}
                              className="avatar"
                              userName={channel.ownerName}
                              srcName={channel.avatar as string}
                              altName={channel.name}
                            />
                            <div className={styles.team}>
                              <div
                                className={cls(
                                  styles.team_title,
                                  styles.elipsis,
                                )}
                              >
                                {channel.name}
                              </div>
                              <div className={cls(styles.team_subtitle)}>
                                {t("Channel")}
                              </div>
                            </div>
                            <span className={styles.checkbox}>
                              {selectedChannelsId.includes(channel.id) ? (
                                <i
                                  className={cls(
                                    "fas fa-check-circle",
                                    styles.checked,
                                  )}
                                />
                              ) : (
                                <i
                                  className={cls(
                                    "far fa-circle",
                                    styles.unchecked,
                                  )}
                                />
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              return null;
            }
          })}
      </div>
    </ApplyTemplateListsModal>
  );
};

export default ChannelSelectModal;
